// src/Components/RfqList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

// +++ A simple chat icon component (SVG) +++
const ChatIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
    </svg>
);


const RfqList = () => {
    const [rfqs, setRfqs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchRfqs = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get('/rfqs/');
            setRfqs(response.data || []);
        } catch (err) {
            console.error("Error fetching RFQs:", err);
            setError("Failed to load RFQs. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRfqs();
    }, [fetchRfqs]);

    const filteredRfqs = rfqs.filter(rfq => {
        const term = searchTerm.toLowerCase();
        return (
            rfq.name.toLowerCase().includes(term) ||
            rfq.id.toString().includes(term) ||
            (rfq.countries && rfq.countries.some(country => country.toLowerCase().includes(term)))
        );
    });


    if (isLoading) {
        // ... loading state JSX is unchanged ...
        return (
            <div className="flex justify-center items-center h-screen bg-gray-100">
                <div className="text-center">
                    <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-700">Loading RFQs...</h2>
                </div>
            </div>
        );
    }

    if (error) {
        // ... error state JSX is unchanged ...
         return (
            <div className="text-center p-10 bg-red-50 text-red-700 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-2">Error Loading RFQs</h3>
                <p>{error}</p>
                <button 
                    onClick={fetchRfqs} 
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-full mx-auto xl:max-w-7xl">
                {/* ... header and search bar are unchanged ... */}
                <header className="mb-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900">All RFQs</h1>
                        <p className="mt-1 text-lg text-gray-600">Browse and manage existing Requests for Quotation.</p>
                    </div>
                    <Link to="/rfqs/new" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Create New RFQ
                    </Link>
                </header>

                <div className="mb-6">
                    <input
                        type="text"
                        placeholder="Search RFQs by ID, Name, or Country..."
                        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {filteredRfqs.length === 0 ? (
                     <div className="text-center py-10 bg-white rounded-lg shadow">
                        <p className="text-gray-500 italic">
                             {rfqs.length === 0 ? "No RFQs found. Create one to get started!" : "No RFQs match your search criteria."}
                        </p>
                    </div>
                ) : (
                    <div className="bg-white shadow-xl rounded-lg overflow-x-auto border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                           {/* ... table head is unchanged ... */}
                           <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RFQ ID</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Countries</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Methodologies</th>
                                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Viewed</th>
                                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {filteredRfqs.map((rfq) => {
                                    // Calculate counts from nested invitation data
                                    const submittedCount = rfq.invitations?.filter(inv => inv.status === 'SUBMITTED').length ?? 0;
                                    const viewedCount = rfq.invitations?.filter(inv => inv.status === 'VIEWED' || inv.status === 'SUBMITTED').length ?? 0;
                                    const sentCount = rfq.invitations?.length ?? 0;

                                    return (
                                    <tr key={rfq.id} className="hover:bg-gray-50 transition-colors duration-150">
                                        {/* ... other table cells are unchanged ... */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <Link to={`/rfqs/${rfq.id}`} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                                                #{rfq.id}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 truncate max-w-xs" title={rfq.name}>
                                            {rfq.name || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {rfq.countries && rfq.countries.length > 0 ? rfq.countries.join(', ') : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {rfq.methodologies && rfq.methodologies.length > 0 ? rfq.methodologies.join(', ') : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{sentCount}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{viewedCount}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            {submittedCount > 0 ? (
                                                <span className="font-semibold text-green-600">{submittedCount}</span>
                                            ) : ( 0 )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(rfq.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                rfq.status === 'AWARDED' ? 'bg-green-100 text-green-800' :
                                                rfq.status === 'OPEN' ? 'bg-blue-100 text-blue-800' :
                                                rfq.status === 'DRAFT' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {rfq.status ? rfq.status.replace('_', ' ') : 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {/* +++ UPDATED ACTIONS COLUMN +++ */}
                                            <div className="flex items-center space-x-4">
                                                <Link
                                                    to={`/rfqs/${rfq.id}`}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                    title="View Details"
                                                >
                                                    Details
                                                </Link>
                                                
                                                {/* SHOW DISCUSSION ICON IF AWARDED */}
                                                {rfq.status === 'AWARDED' && (
                                                    <Link
                                                        to={`/rfqs/${rfq.id}#discussion`}
                                                        className="text-gray-500 hover:text-purple-600"
                                                        title="Open Discussion"
                                                    >
                                                        <ChatIcon />
                                                    </Link>
                                                )}

                                                {submittedCount > 0 && rfq.status !== 'AWARDED' && (
                                                    <Link
                                                        to={`/rfqs/${rfq.id}/responses-summary`}
                                                        className="text-green-600 hover:text-green-800"
                                                    >
                                                        Compare
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RfqList;